import { Box, Checkbox, Icon, IconButton } from '@chakra-ui/react';
import { CellContext, ColumnMeta, HeaderContext } from '@tanstack/react-table';
import { BiCaretDown, BiCaretRight } from 'react-icons/bi';
import { RiProhibitedLine } from 'react-icons/ri';

import { FilterType } from '../../../_openapi/portfolio-optimizer';
import { getColumnLabel } from '../../../utils/tableUtils';
import { BooleanCell } from '../BooleanCell';
import { DateCell } from '../DateCell';
import { DateTimeCell } from '../DateTimeCell';
import { MoneyCell, MoneyFormatProps } from '../MoneyCell';
import { NumberCell, NumberFormatProps } from '../NumberCell';
import { TextCell } from '../TextCell';

export const baseCol = ({ id, header }: { id: string; header?: string }) => ({
    id: id,
    header: header || getColumnLabel(id),
});

type ColBaseProps<T> = {
    id: string;
    header?: string;
    meta?: ColumnMeta<T, any>;
};

export const textCol = <T,>({ id, meta, header }: ColBaseProps<T>) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue, table, column }: CellContext<T, string | undefined>) => {
            const colFilter = table.getState().columnFilters.find((filter) => column.id === filter.id);
            const highlight = colFilter?.value as string[] | undefined;
            return <TextCell value={getValue()} highlight={highlight} />;
        },
        meta,
    };
};

export const numberCol = <T,>({ id, meta, header, format }: ColBaseProps<T> & { format?: NumberFormatProps }) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue }: CellContext<T, string | number | undefined>) => {
            return <NumberCell value={getValue()} {...format} />;
        },
        meta: {
            ...meta,
            filterType: FilterType.NUMBER,
        },
    };
};

export const roundedNumberCol = <T,>({ id, meta, header }: ColBaseProps<T>) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue }: CellContext<T, string | number | undefined>) => {
            const value = getValue();
            const roundedValue = typeof value === 'number' ? Math.round(value) : value;
            return <NumberCell value={roundedValue} fractionDigits={0} />;
        },
        meta: {
            ...meta,
            filterType: FilterType.NUMBER,
        },
    };
};

export const percentageCol = <T,>({ id, meta, header, decimals = 1 }: ColBaseProps<T> & { decimals?: number }) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue }: CellContext<T, string | number | undefined>) => {
            const value = getValue();
            if (typeof value === 'number') {
                const percentage = (value * 100).toFixed(decimals);
                return <TextCell value={percentage + '%'} textAlign="right" />;
            }
            return null;
        },
        meta: {
            ...meta,
            filterType: FilterType.NUMBER,
            isPercentage: true,
            percentDecimals: decimals,
        },
    };
};

export const moneyCol = <T,>({ id, meta, header, format }: ColBaseProps<T> & { format?: MoneyFormatProps }) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue }: CellContext<T, string | number | undefined>) => {
            return <MoneyCell value={getValue()} {...format} />;
        },
        meta: {
            ...meta,
            filterType: FilterType.NUMBER,
        },
    };
};

export const dateCol = <T,>({ id, meta, header }: ColBaseProps<T>) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue }: CellContext<T, string | undefined>) => {
            return <DateCell dateString={getValue()} />;
        },
        meta: {
            ...meta,
            filterType: FilterType.DATE,
        },
    };
};
export const dateTimeCol = <T,>({ id, meta, header }: ColBaseProps<T>) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue }: CellContext<T, string | undefined>) => {
            return <DateTimeCell dateString={getValue()} />;
        },
        meta: {
            ...meta,
            filterType: FilterType.DATE,
        },
    };
};

export const booleanCol = <T,>({ id, meta, header }: ColBaseProps<T>) => {
    return {
        ...baseCol({ id, header }),
        cell: ({ getValue }: CellContext<T, boolean | undefined>) => {
            return <BooleanCell value={getValue()} />;
        },
        meta: {
            ...meta,
            filterType: FilterType.BOOLEAN,
        },
    };
};

export const expanderCol = <T,>() => ({
    id: 'expander',
    header: ({ table }: HeaderContext<T, any>) => (
        <IconButton
            size="xs"
            onClick={table.getToggleAllRowsExpandedHandler()}
            icon={table.getIsAllRowsExpanded() ? <Icon as={BiCaretDown} /> : <Icon as={BiCaretRight} />}
            aria-label="toggle-all-columns"
            style={{ marginLeft: '-4px' }}
        />
    ),
    cell: ({ row }: CellContext<T, any>) => (
        <IconButton
            size="xs"
            onClick={row.getToggleExpandedHandler()}
            icon={row.getIsExpanded() ? <Icon as={BiCaretDown} /> : <Icon as={BiCaretRight} />}
            aria-label="toggle-all-columns"
            isDisabled={!row.getCanExpand()}
        />
    ),
    size: 40,
    meta: {
        isDisplayCol: true,
    },
});

export const selectCol = <T,>() => ({
    id: 'select',
    header: ({ table }: HeaderContext<T, any>) => {
        const enabledRows = table.getFilteredRowModel().flatRows;
        const isAllSelected = enabledRows.every((row) => row.getIsSelected());
        const isSomeSelected = enabledRows.some((row) => row.getIsSelected());
        const onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
            const checked = ev.currentTarget.checked;
            enabledRows.forEach((row) => {
                row.toggleSelected(checked);
            });
        };

        return (
            <Checkbox
                colorScheme="teal"
                isChecked={isAllSelected}
                isIndeterminate={!isAllSelected && isSomeSelected}
                onChange={onChange}
            />
        );
    },
    cell: ({ row }: CellContext<T, any>) => {
        const enabledRows = row.subRows;
        let isAllSelected;
        let isSomeSelected;
        let onChange;

        if (enabledRows.length) {
            isAllSelected = enabledRows.every((r) => r.getIsSelected());
            isSomeSelected = enabledRows.some((r) => r.getIsSelected());
            onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
                const checked = ev.currentTarget.checked;
                enabledRows.forEach((r) => {
                    r.toggleSelected(checked);
                });
            };
        } else {
            isAllSelected = row.getIsSelected();
            isSomeSelected = row.getIsSomeSelected();
            onChange = row.getToggleSelectedHandler();
        }

        return (
            <Checkbox
                colorScheme="teal"
                isChecked={isAllSelected}
                isIndeterminate={!isAllSelected && isSomeSelected}
                onChange={onChange}
            />
        );
    },
    meta: {
        isDisplayCol: true,
    },
});

export const excludeTradesCol = <T extends { trades: any[] }>(excludeTrades: (trades: any[]) => void) => ({
    id: 'exclude-trades',
    cell: ({ row }: CellContext<T, any>) => (
        <Box textAlign="right">
            <IconButton
                size="xs"
                onClick={() => excludeTrades(row.original.trades)}
                icon={<Icon boxSize={4} as={RiProhibitedLine} />}
                aria-label="exclude-trades"
                colorScheme="red"
            />
        </Box>
    ),
    size: 40,
    meta: {
        isDisplayCol: true,
    },
});
